export const SELF_LEADERSHIP_QUESTIONS = [
    'I make decisions even when I don’t have all the information.',
    'I stay calm and clear, even under pressure.',
    'I set clear priorities and stick to them.',
    'I regularly reflect on my patterns and behaviour.',
    'I communicate my needs and boundaries clearly.',
    'I take responsibility for my results — even when things are difficult.',
    'I can motivate myself without external pressure.',
    'I handle criticism constructively.',
    'I intentionally make time for my personal development.',
    'I act in alignment with my values — even under pressure.'
] as const

export const SELF_LEADERSHIP_SCALE = [
    { value: 1 as const, label: 'Strongly disagree', short: 'Str. disagree' },
    { value: 2 as const, label: 'Disagree', short: 'Disagree' },
    { value: 3 as const, label: 'Neutral', short: 'Neutral' },
    { value: 4 as const, label: 'Agree', short: 'Agree' },
    { value: 5 as const, label: 'Strongly agree', short: 'Str. agree' }
] as const

export type SelfLeadershipResultBand = {
    id: string
    rangeLabel: string
    title: string
    strengths: string[]
    growth: string[]
    min: number
    max: number
}

export const SELF_LEADERSHIP_RESULT_BANDS: SelfLeadershipResultBand[] = [
    {
        id: 'strong',
        rangeLabel: '41–50 points',
        title: 'Strong Self-Leadership',
        min: 41,
        max: 50,
        strengths: ['Clear inner direction', 'Decisiveness', 'Emotional stability', 'High self-awareness'],
        growth: ['Delegate more strategically', 'Build in intentional recovery time', 'Expand your leadership impact']
    },
    {
        id: 'solid',
        rangeLabel: '31–40 points',
        title: 'Solid Foundation',
        min: 31,
        max: 40,
        strengths: ['Good structure in self-management', 'Willingness to take responsibility', 'Emerging clarity'],
        growth: ['Increase consistency in decisions', 'Set firmer boundaries', 'Strengthen self-motivation']
    },
    {
        id: 'developing',
        rangeLabel: '21–30 points',
        title: 'Developing Balance',
        min: 21,
        max: 30,
        strengths: ['Awareness of growth areas', 'Situational self-leadership', 'Reflective mindset'],
        growth: ['Define priorities more clearly', 'Build emotional stability', 'Strengthen decision confidence']
    },
    {
        id: 'growth',
        rangeLabel: '10–20 points',
        title: 'Growth Potential',
        min: 10,
        max: 20,
        strengths: ['Openness to change', 'Early self-reflection'],
        growth: [
            'Clarify goals',
            'Take ownership consciously',
            'Build structure and discipline',
            'Develop inner confidence'
        ]
    }
]

export function selfLeadershipRatingLabel(value: number): string {
    const row = SELF_LEADERSHIP_SCALE.find((s) => s.value === value)
    return row?.label ?? String(value)
}

export function bandForScore(total: number): SelfLeadershipResultBand {
    return (
        SELF_LEADERSHIP_RESULT_BANDS.find((b) => total >= b.min && total <= b.max) ??
        SELF_LEADERSHIP_RESULT_BANDS[SELF_LEADERSHIP_RESULT_BANDS.length - 1]
    )
}

export function totalFromAnswers(answers: number[]): number {
    return answers.reduce((s, a) => s + a, 0)
}
